import type { ReactNode } from 'react'

import { useGsap } from '@/hooks/use-gsap'
import { SplitWords } from '@/components/generals/split-words'

export type DocumentSection = {
    heading: string
    paragraphs: string[]
}

interface DocumentPageProps {
    titleLines: string[]
    standfirst: string
    updatedOn: string
    sections: DocumentSection[]
}

/** Shared layout for the policy pages, so they share one rhythm and one set of animations. */
export const DocumentPage = ({
    titleLines,
    standfirst,
    updatedOn,
    sections
}: DocumentPageProps): ReactNode => {
    const scope = useGsap<HTMLDivElement>(({ gsap, select, reduced }) => {
        if (reduced) return

        const intro = gsap.timeline({ defaults: { ease: 'nuvea' } })

        intro
            .from(select('[data-doc-title] .reveal-mask > span'), {
                yPercent: 115,
                duration: 1.1,
                stagger: 0.06
            })
            .from(
                select('[data-doc-intro] > *'),
                { y: 22, opacity: 0, duration: 0.85, stagger: 0.08 },
                '-=0.7'
            )

        select('[data-doc-section]').forEach((section) => {
            gsap.from(section, {
                y: 32,
                opacity: 0,
                duration: 0.9,
                ease: 'nuvea',
                scrollTrigger: { trigger: section, start: 'top 90%' }
            })
        })
    })

    return (
        <div ref={scope} className='shell-x mx-auto w-full max-w-[1760px] pt-10 pb-24 md:pt-16 md:pb-32'>
            <SplitWords
                data-doc-title=''
                as='h1'
                lines={titleLines}
                className='type-display max-w-[14ch] text-ink'
            />

            <div className='mt-10 grid grid-cols-1 gap-8 md:mt-14 md:grid-cols-[10rem_1fr] md:gap-12 lg:grid-cols-[14rem_minmax(0,64ch)_1fr]'>
                <p className='type-label h-fit w-fit rounded-full bg-sage px-3 py-1.5 text-ink'>
                    Updated {updatedOn}
                </p>

                <div data-doc-intro=''>
                    <p className='type-lead text-ink'>
                        {standfirst}
                    </p>
                </div>
            </div>

            <div className='mt-16 max-w-[72ch] md:mt-24'>
                {sections.map((section) => (
                    <section
                        key={section.heading}
                        data-doc-section=''
                        className='border-t border-stone-line pt-8 not-last:pb-12'
                    >
                        <h2 className='type-h3 text-ink'>
                            {section.heading}
                        </h2>

                        <div className='mt-5 flex flex-col gap-4'>
                            {section.paragraphs.map((paragraph) => (
                                <p key={paragraph.slice(0, 40)} className='type-copy leading-relaxed text-slate-soft'>
                                    {paragraph}
                                </p>
                            ))}
                        </div>
                    </section>
                ))}
            </div>
        </div>
    )
}
